import { useEffect, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { SaveRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import type { LicenseMappingDto } from '../api/types';

/**
 * Lizenz-Mapping — als Tab innerhalb der Einstellungen (Admin-geschützt durch
 * die umgebende Settings-Seite). Listet alle Tenant-Lizenzen und erlaubt das
 * Vergeben eines freien Anzeigenamens. Leeres Feld = eingebauter Standardname.
 * Die Namen wirken überall, wo Lizenzen angezeigt werden (User anlegen,
 * User-Insights …).
 */
export function LicenseSettings() {
  const api = useApi();
  const state = useAsync(() => api.getLicenseMappings(), [api]);
  const [rows, setRows] = useState<LicenseMappingDto[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingSku, setSavingSku] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) {
      setRows(state.data);
      setDrafts(Object.fromEntries(state.data.map((r) => [r.skuPartNumber, r.customName ?? ''])));
    }
  }, [state.data]);

  async function save(row: LicenseMappingDto) {
    const value = (drafts[row.skuPartNumber] ?? '').trim();
    setSavingSku(row.skuPartNumber);
    setError(null);
    try {
      await api.setLicenseMapping(row.skuPartNumber, { customName: value || null });
      setRows((rs) =>
        rs.map((r) => (r.skuPartNumber === row.skuPartNumber ? { ...r, customName: value || null } : r)),
      );
      setDrafts((d) => ({ ...d, [row.skuPartNumber]: value }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingSku(null);
    }
  }

  return (
    <div style={{ maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        Anzeigenamen für die Lizenzen des Tenants frei vergeben. Ein leeres Feld verwendet den Standardnamen (in Klammern
        angedeutet). Die Bezeichnung gilt überall, wo Lizenzen erscheinen — etwa beim Anlegen von Nutzern und in den
        User-Insights.
      </Caption1>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {state.loading ? <Spinner size="tiny" label="Lade Lizenzen…" /> : null}
      {state.error ? (
        <MessageBar intent="error">
          <MessageBarBody>{state.error.message}</MessageBarBody>
        </MessageBar>
      ) : null}
      {!state.loading && !state.error && rows.length === 0 ? (
        <Body1>Keine Lizenzen im Tenant gefunden.</Body1>
      ) : null}

      {rows.map((row) => {
        const draft = drafts[row.skuPartNumber] ?? '';
        const dirty = draft !== (row.customName ?? '');
        const effective = (row.customName ?? '').trim() || row.defaultName;
        return (
          <div
            key={row.skuId}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: tokens.spacingHorizontalM,
              padding: tokens.spacingVerticalM,
              border: `1px solid ${tokens.colorNeutralStroke2}`,
              borderRadius: tokens.borderRadiusLarge,
            }}
          >
            <div style={{ flex: '1 1 240px', minWidth: '200px' }}>
              <Text weight="semibold">{effective}</Text>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: tokens.spacingHorizontalS,
                  marginTop: '2px',
                }}
              >
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{row.skuPartNumber}</Caption1>
                <Badge appearance="tint" color="informative">
                  {row.available} frei / {row.consumed} belegt
                </Badge>
              </div>
            </div>

            <Input
              value={draft}
              placeholder={row.defaultName}
              aria-label={`Anzeigename für ${row.skuPartNumber}`}
              onChange={(_, d) => setDrafts((s) => ({ ...s, [row.skuPartNumber]: d.value }))}
              style={{ flex: '1 1 220px', minWidth: '200px' }}
            />

            <Button
              appearance="primary"
              icon={<SaveRegular />}
              disabled={!dirty || savingSku === row.skuPartNumber}
              onClick={() => save(row)}
            >
              {savingSku === row.skuPartNumber ? 'Speichert…' : 'Speichern'}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
